import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Shared launch-state reader. Fetches /api/launch exactly once for the whole app
// (deduped) and hands the result to every subscriber, so the pre-launch gate and the
// Layout shell agree without firing the call twice.
let _cache = null;
let _promise = null;
const _subs = new Set();

// Baked in at build time; used only when /api/launch is unreachable twice.
const LAUNCH_AT_MS = Date.parse(process.env.REACT_APP_LAUNCH_AT || "");

function fallbackState() {
  const live = Number.isNaN(LAUNCH_AT_MS) ? false : Date.now() >= LAUNCH_AT_MS;
  return { live, launch_at: process.env.REACT_APP_LAUNCH_AT || null, server_now: new Date().toISOString(), fallback: true };
}

function fetchLaunch() {
  const get = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/launch`);
  return get().catch(() => new Promise((r) => setTimeout(r, 1500)).then(get));
}

export function useLaunch() {
  const [launch, setLaunch] = useState(_cache);
  useEffect(() => {
    if (_cache) { setLaunch(_cache); return undefined; }
    _subs.add(setLaunch);
    if (!_promise) {
      _promise = fetchLaunch()
        .then(({ data }) => { _cache = data; })
        .catch(() => { _cache = fallbackState(); })
        .finally(() => { _subs.forEach((fn) => fn(_cache)); });
    }
    return () => { _subs.delete(setLaunch); };
  }, []);
  return launch;
}

// Live Early Bird "up to X% off" figure for pre-launch copy; null until known.
export function useLaunchPercent() {
  const launch = useLaunch();
  const { i18n } = useTranslation();
  const pct = launch?.early_bird_max_pct;
  if (pct == null) return null;
  return i18n.language?.startsWith("bn") ? new Intl.NumberFormat("bn").format(pct) : String(pct);
}
